import { Link } from "react-router-dom";
import { useFullCurrentUser } from "@/hooks/useCurrentUser";
import { useProductFeatures } from "@/hooks/useProductFeatures";
import { useMyEnrollments } from "@/hooks/useMyEnrollments";
import { Box, Paper, Typography } from "@mui/material";
import { isLadderCompletionDebugAllowed } from "./LadderCompletionDebug";

export const HlDebug = () => {
  const { activeUserRecord } = useFullCurrentUser();
  const isClimber = activeUserRecord?.role === "climber";
  const {
    productPlans,
    activeProduct,
    isLoading: plansLoading,
    isDebugOverride,
  } = useProductFeatures();
  const { enrollments, loading: enrollmentsLoading } = useMyEnrollments();
  const sectionEnrollments = enrollments.filter(
    (enrollment) => enrollment.scope === "SECTION",
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {isClimber && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Climber product plans
            {isDebugOverride ? " (debug override)" : ""}
          </Typography>
          {plansLoading ? (
            <Typography color="text.secondary">Loading…</Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Active product: {activeProduct?.code ?? "—"}
              </Typography>
              {productPlans.length === 0 ? (
                <Typography color="text.secondary">No product plans</Typography>
              ) : (
                <Typography
                  component="pre"
                  sx={{ fontSize: "0.875rem", fontFamily: "monospace" }}
                >
                  {JSON.stringify(productPlans, null, 2)}
                </Typography>
              )}
            </>
          )}
        </Paper>
      )}
      {isClimber && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Climber section
          </Typography>
          {enrollmentsLoading ? (
            <Typography color="text.secondary">Loading…</Typography>
          ) : sectionEnrollments.length === 0 ? (
            <Typography color="text.secondary">No section</Typography>
          ) : (
            sectionEnrollments.map((enrollment) => (
              <Box key={enrollment.id} sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {enrollment.entityName ?? "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {enrollment.entityId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {enrollment.id}
                </Typography>
              </Box>
            ))
          )}
        </Paper>
      )}
      <ul>
        <li>
          <Link to="facets">Facets</Link> - Info about current user facets
        </li>
        <li>
          <Link to="awards">Awards</Link> - Info about current user awards
        </li>
        <li>
          <Link to="next-steps">Next Steps</Link> - Debug next steps
          recommendation engine
        </li>
        <li>
          <Link to="palette">Palette</Link> - Overall app color palette
        </li>
        <li>
          <Link to="flags">Flags</Link> - Debugging feature flags (and product
          plan)
        </li>
        <li>
          <Link to="reset-user">Reset User</Link> - DANGER: Reset current user
          data
        </li>
        <li>
          <Link to="ms-hats-country">Hats Debug</Link> - List 10-at-a-time hats
          by region type
        </li>
        <li>
          <Link to="pdf-preview">PDF Preview</Link> - Preview Career Plan PDF
          without downloading
        </li>
        <li>
          <Link to="course-plan-pdf-preview">Course Plan PDF Preview</Link> -
          Preview Course Plan PDF with fake data for live editing
        </li>
        <li>
          <Link to="certificates">Certificates</Link> - Preview all certificate
          PDFs (including unearned ones)
        </li>
        <li>
          <Link to="celebrations">Celebrations</Link> - Pick and preview any
          celebration (badges, shout-outs, hat packs, climbs, courses,
          certificates, clusters) without persisting acknowledgements
        </li>
        {isLadderCompletionDebugAllowed() && (
          <li>
            <Link to="ladder-completion">Ladder Completion</Link> - DEV/LOCAL
            only: bulk mark ladders complete to test celebration flows
          </li>
        )}
        <li>
          <Link to="/ibm-sync">IBM Course Data Sync</Link> - Synchronize IBM
          lesson data with Hats &amp; Ladders
        </li>
      </ul>
    </Box>
  );
};
